import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/DashboardLayout";
import { formatApiError } from "../lib/formatApiError";
import { API } from "../lib/constants";

function CreateOrgSection({ onCreated }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/org/create`, { name });
      onCreated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto">
      <div className="bg-white border border-gray-200 rounded-xl p-8 text-center" data-testid="create-org-section">
        <div className="w-16 h-16 bg-[#0066FF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl font-black text-[#0066FF]">T</span>
        </div>
        <h2 className="text-xl font-bold mb-2">Create Your Team</h2>
        <p className="text-gray-600 mb-6">Collaborate on content with your team. Share credits and view each other's work.</p>
        {error && <div className="bg-red-50 border border-red-500 text-red-700 p-3 mb-4 text-sm rounded-lg">{error}</div>}
        <form onSubmit={handleCreate} className="space-y-4">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Organization name" className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg text-center" required data-testid="org-name-input" />
          <button type="submit" disabled={loading} className="w-full bg-[#0066FF] text-white py-3 font-bold hover:bg-[#0052CC] transition-colors disabled:opacity-50 rounded-lg" data-testid="create-org-btn">
            {loading ? "Creating..." : "Create Organization"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PendingInvitesSection({ invites, onAction }) {
  const handleAccept = async (token) => {
    try {
      await axios.post(`${API}/org/invites/${token}/accept`);
      onAction();
    } catch (err) { console.error("Accept invite failed:", err); }
  };
  const handleDecline = async (token) => {
    try {
      await axios.post(`${API}/org/invites/${token}/decline`);
      onAction();
    } catch (err) { console.error("Decline invite failed:", err); }
  };

  if (invites.length === 0) return null;

  return (
    <div className="bg-[#0066FF]/5 border border-[#0066FF]/20 rounded-xl p-6 mb-8" data-testid="pending-invites">
      <h2 className="font-bold mb-4">Pending Invitations</h2>
      <div className="space-y-3">
        {invites.map((inv) => (
          <div key={inv.token} className="bg-white p-4 rounded-lg flex items-center justify-between border">
            <div>
              <p className="font-medium">{inv.org_name}</p>
              <p className="text-sm text-gray-500">Role: {inv.role}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleAccept(inv.token)} className="bg-[#0066FF] text-white px-4 py-2 text-sm font-bold rounded-lg" data-testid={`accept-invite-${inv.token}`}>Accept</button>
              <button onClick={() => handleDecline(inv.token)} className="border border-gray-300 px-4 py-2 text-sm font-bold rounded-lg" data-testid={`decline-invite-${inv.token}`}>Decline</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrgDashboard({ orgData, onRefresh }) {
  const { user } = useAuth();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [teamContent, setTeamContent] = useState([]);
  const [showContent, setShowContent] = useState(false);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError("");
    setInviteMsg("");
    setInviteLoading(true);
    try {
      const { data } = await axios.post(`${API}/org/invite`, { email: inviteEmail, role: inviteRole });
      setInviteMsg(data.message);
      setInviteEmail("");
      onRefresh();
    } catch (err) {
      setInviteError(formatApiError(err.response?.data?.detail));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from the team?`)) return;
    try {
      await axios.delete(`${API}/org/members/${encodeURIComponent(email)}`);
      onRefresh();
    } catch (err) { console.error("Remove member failed:", err); }
  };

  const handleRoleChange = async (email, newRole) => {
    try {
      await axios.put(`${API}/org/members/${encodeURIComponent(email)}/role`, { role: newRole });
      onRefresh();
    } catch (err) { console.error("Role change failed:", err); }
  };

  const handleLeave = async () => {
    if (!window.confirm("Leave this organization?")) return;
    try {
      await axios.post(`${API}/org/leave`);
      onRefresh();
    } catch (err) { console.error("Leave org failed:", err); }
  };

  const loadTeamContent = async () => {
    if (showContent) { setShowContent(false); return; }
    try {
      const { data } = await axios.get(`${API}/org/content`);
      setTeamContent(data);
      setShowContent(true);
    } catch (err) { console.error("Load team content failed:", err); }
  };

  const isOwnerOrAdmin = orgData.my_role === "owner" || orgData.my_role === "admin";

  return (
    <div className="space-y-6">
      {/* Org Header */}
      <div className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white p-8 rounded-2xl" data-testid="org-header">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center">
            <span className="text-3xl font-black text-[#0066FF]">{orgData.org.name[0].toUpperCase()}</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold">{orgData.org.name}</h2>
            <p className="text-white/70 text-sm">Your role: {orgData.my_role} &bull; {orgData.members?.length || 0} members</p>
          </div>
        </div>
      </div>

      {/* Members */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="members-section">
        <h2 className="text-lg font-bold mb-4">Team Members</h2>
        <div className="space-y-3">
          {orgData.members?.map((m) => (
            <div key={m.email} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg" data-testid={`member-${m.email}`}>
              <div>
                <p className="font-medium">{m.name}</p>
                <p className="text-sm text-gray-500">{m.email}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${m.org_role === "owner" ? "bg-yellow-100 text-yellow-700" : m.org_role === "admin" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                  {m.org_role?.toUpperCase()}
                </span>
                {orgData.my_role === "owner" && m.email !== user?.email && (
                  <div className="flex gap-2">
                    <select value={m.org_role} onChange={(e) => handleRoleChange(m.email, e.target.value)} className="text-xs border rounded px-2 py-1" data-testid={`role-select-${m.email}`}>
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button onClick={() => handleRemove(m.email)} className="text-xs text-red-500 hover:text-red-700 font-bold" data-testid={`remove-${m.email}`}>Remove</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pending Invites */}
        {orgData.invites?.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm font-bold text-gray-500 mb-2">Pending Invites</p>
            {orgData.invites.map((inv) => (
              <div key={inv.email} className="flex items-center justify-between py-2">
                <span className="text-sm">{inv.email}</span>
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-bold">PENDING</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite Form */}
      {isOwnerOrAdmin && (
        <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="invite-section">
          <h2 className="text-lg font-bold mb-4">Invite Team Member</h2>
          {inviteMsg && <div className="bg-green-50 border border-green-500 text-green-700 p-3 mb-4 text-sm rounded-lg">{inviteMsg}</div>}
          {inviteError && <div className="bg-red-50 border border-red-500 text-red-700 p-3 mb-4 text-sm rounded-lg">{inviteError}</div>}
          <form onSubmit={handleInvite} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Email</label>
              <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" required data-testid="invite-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Role</label>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="invite-role">
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button type="submit" disabled={inviteLoading} className="bg-[#0066FF] text-white px-6 py-3 font-bold hover:bg-[#0052CC] transition-colors disabled:opacity-50 rounded-lg" data-testid="send-invite-btn">
              {inviteLoading ? "Sending..." : "Send Invite"}
            </button>
          </form>
        </div>
      )}

      {/* Team Content */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="team-content-section">
        <button onClick={loadTeamContent} className="flex items-center gap-2 text-lg font-bold mb-4" data-testid="toggle-team-content">
          <span className={`transform transition-transform ${showContent ? "rotate-90" : ""}`}>&#9654;</span>
          Team Content History
        </button>
        {showContent && (
          <div className="space-y-3">
            {teamContent.length === 0 ? (
              <p className="text-gray-500 text-sm">No team content generated yet.</p>
            ) : teamContent.map((item, idx) => (
              <div key={`${item.created_at}-${idx}`} className="border border-gray-100 p-4 rounded-lg" data-testid="team-content-item">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-xs font-bold bg-[#0066FF]/10 text-[#0066FF] rounded">{item.type}</span>
                    <span className="text-sm font-medium">{item.author}</span>
                  </div>
                  <span className="text-xs text-gray-400">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ""}</span>
                </div>
                <p className="text-sm text-gray-600 truncate">{item.prompt}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Leave Org */}
      {orgData.my_role !== "owner" && (
        <div className="text-right">
          <button onClick={handleLeave} className="text-sm text-red-500 hover:text-red-700 font-bold" data-testid="leave-org-btn">Leave Organization</button>
        </div>
      )}
    </div>
  );
}

export default function TeamPage() {
  const [orgData, setOrgData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [orgRes, invRes] = await Promise.all([
        axios.get(`${API}/org/me`),
        axios.get(`${API}/org/invites`)
      ]);
      setOrgData(orgRes.data.org ? orgRes.data : null);
      setInvites(invRes.data);
    } catch (err) { console.error("Load org data failed:", err); } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Team</h1>
          <p className="text-gray-600">Manage your organization and collaborate on content</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <>
            <PendingInvitesSection invites={invites} onAction={loadData} />
            {orgData ? (
              <OrgDashboard orgData={orgData} onRefresh={loadData} />
            ) : (
              <CreateOrgSection onCreated={loadData} />
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
